import React, { Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
} from "react-router";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { installSessionWatcher } from "@/lib/session/sessionWatcher";
import { LoginPage } from "@/components/authentication/login/loginPage";
import { RegisterPage } from "@/components/authentication/signup/RegisterPage";
import { InvitePage } from "@/components/authentication/signup/InvitePage";
import { VerificationPage } from "@/components/authentication/verfication/VerificationPage";
import { VerificationSentPage } from "@/components/authentication/signup/VerificationSent";
import { ForgotPasswordPage } from "@/components/authentication/login/ForgotPasswordPage";
import { ResetPasswordPage } from "@/components/authentication/login/ResetPasswordPage";
import { useAuth } from "@/contexts/AuthProvider";
import { NavigationProvider } from "@/contexts/NavigationProvider";
import { ProtectedCanvasRoute } from "@/contexts/ProtectedCanvasRoute";
import { ColumnConflictDialog } from "@/components/common/ColumnConflictDialog";
import { TypeChangeConfirmDialog } from "@/components/common/TypeChangeConfirmDialog";
import { GlobalStatusIndicator } from "@/components/common/StatusIndicatorContainer";
import { SessionExpiryProvider } from "@/contexts/SessionExpiryContext";
import { SessionExpirySetup } from "@/contexts/SessionExpirySetup";
import { PublicRoute } from "@/contexts/PublicRoute";
import { MainLayout } from "@/components/main/MainLayout";
import { AccessPage } from "@/components/main/pages/AccessPage";
import { OverviewPage } from "@/components/main/pages/OverviewPage";
import { CatalogModelsPage } from "@/components/main/pages/CatalogModelsPage";
import { BillingWelcomePage } from "@/components/main/pages/BillingWelcomePage";
import { SharedWithMePage } from "@/components/main/pages/SharedWithMePage";
import { DatasetsPage } from "@/components/main/pages/DatasetsPage";
import { RecipesPage } from "@/components/main/pages/RecipesPage";
import { ConnectionsPage } from "@/components/main/connections/ConnectionsPage";
import { TrashPage } from "@/components/main/pages/TrashPage";
import { StatusBasedRoute } from "@/contexts/StatusBasedRoute";
import { ConsentGate } from "@/contexts/ConsentGate";
import { ConsentPage } from "@/components/authentication/consent/ConsentPage";
import { SpaceSettingsPage } from "@/components/main/spaces/SpaceSettingsPage";
import { OrgSettingsPage } from "@/components/main/organizations/OrgSettingsPage";
import { OrgCreatePage } from "@/components/main/organizations/OrgCreatePage";
import { OrgCreatedPage } from "@/components/main/organizations/OrgCreatedPage";
import { NotFoundPage } from "@/components/NotFoundPage";
import { ErrorPage } from "@/components/ErrorPage";

// Lazy-loaded heavy routes (code splitting)
const CanvasApp = React.lazy(() =>
  import("@/components/canvas/CanvasApp").then((m) => ({
    default: m.CanvasApp,
  })),
);
const InvitationsPage = React.lazy(() =>
  import("@/components/admin/InvitationsPage").then((m) => ({
    default: m.InvitationsPage,
  })),
);
const AccountsPage = React.lazy(() =>
  import("@/components/admin/AccountsPage").then((m) => ({
    default: m.AccountsPage,
  })),
);
const DataLifecyclePage = React.lazy(() =>
  import("@/components/admin/DataLifecyclePage").then((m) => ({
    default: m.DataLifecyclePage,
  })),
);
const AuditToolingPage = React.lazy(() =>
  import("@/components/admin/AuditToolingPage").then((m) => ({
    default: m.AuditToolingPage,
  })),
);
const ConsentDashboard = React.lazy(() =>
  import("@/components/admin/ConsentDashboard").then((m) => ({
    default: m.ConsentDashboard,
  })),
);
const RemovedAccountsPage = React.lazy(() =>
  import("@/components/admin/RemovedAccountsPage").then((m) => ({
    default: m.RemovedAccountsPage,
  })),
);
const ServiceAccountsPage = React.lazy(() =>
  import("@/components/admin/ServiceAccountsPage").then((m) => ({
    default: m.ServiceAccountsPage,
  })),
);
const DesignPreview = React.lazy(() =>
  import("@/components/_design-preview/DesignPreview").then((m) => ({
    default: m.DesignPreview,
  })),
);

/**
 * Root layout component that wraps all routes.
 * Provides SessionExpiryProvider context and GlobalStatusIndicator.
 */
const RootLayout: React.FC = () => {
  // Here rather than in MainLayout: the canvas route is a SIBLING of the shell,
  // and the canvas tab is the one left open for hours.
  useEffect(() => installSessionWatcher(), []);
  return (
    <SessionExpiryProvider>
      <SessionExpirySetup />
      <Outlet />
      <GlobalStatusIndicator />
      <ColumnConflictDialog />
      <TypeChangeConfirmDialog />
    </SessionExpiryProvider>
  );
};

const AdminRoute: React.FC = () => {
  const { role } = useAuth();
  return role === "mycor_dev_team" ? (
    <Outlet />
  ) : (
    <Navigate to="/overview" replace />
  );
};

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        children: [
          {
            index: true,
            element: (
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            ),
          },
          {
            path: "register",
            element: (
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            ),
          },
          {
            path: "verify",
            element: (
              <PublicRoute>
                <VerificationPage />
              </PublicRoute>
            ),
          },
          {
            path: "verification-sent",
            element: (
              <PublicRoute>
                <VerificationSentPage />
              </PublicRoute>
            ),
          },
          {
            path: "forgot-password",
            element: (
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            ),
          },
          {
            path: "reset-password/:token",
            element: (
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            ),
          },
          {
            path: "invite/:token",
            element: (
              <PublicRoute>
                <InvitePage />
              </PublicRoute>
            ),
          },
        ],
      },
      {
        path: "/consent",
        element: (
          <StatusBasedRoute requiredStatus="active" element={<ConsentPage />} />
        ),
      },
      {
        path: "/",
        element: (
          <StatusBasedRoute
            requiredStatus="active"
            element={
              <ConsentGate
                element={
                  <NavigationProvider>
                    <ErrorBoundary fallback={<ErrorPage />}>
                      <MainLayout />
                    </ErrorBoundary>
                  </NavigationProvider>
                }
              />
            }
          />
        ),
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <OverviewPage /> },
          { path: "billing/welcome", element: <BillingWelcomePage /> },
          { path: "shared", element: <SharedWithMePage /> },
          { path: "datasets", element: <DatasetsPage /> },
          { path: "catalog/models", element: <CatalogModelsPage /> },
          { path: "recipes", element: <RecipesPage /> },
          { path: "connections", element: <ConnectionsPage /> },
          { path: "spaces/:space_id", element: <SpaceSettingsPage /> },
          { path: "organizations/new", element: <OrgCreatePage /> },
          {
            path: "organizations/:org_id/created",
            element: <OrgCreatedPage />,
          },
          { path: "organizations/:org_id", element: <OrgSettingsPage /> },
          { path: "access", element: <AccessPage /> },
          { path: "trash", element: <TrashPage /> },
          {
            path: "admin",
            element: <AdminRoute />,
            children: [
              { index: true, element: <Navigate to="invitations" replace /> },
              {
                path: "invitations",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <InvitationsPage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "accounts",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <AccountsPage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "audit",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <AuditToolingPage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "removed-accounts",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <RemovedAccountsPage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "service-accounts",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <ServiceAccountsPage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "consent",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <ConsentDashboard />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
              {
                path: "data-lifecycle",
                element: (
                  <ErrorBoundary fallback={<ErrorPage />}>
                    <Suspense>
                      <DataLifecyclePage />
                    </Suspense>
                  </ErrorBoundary>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "/canvas/:model_id",
        element: (
          <StatusBasedRoute
            requiredStatus="active"
            element={
              <ConsentGate
                element={
                  <NavigationProvider>
                    <ProtectedCanvasRoute>
                      <ErrorBoundary fallback={<ErrorPage />}>
                        <Suspense>
                          <CanvasApp />
                        </Suspense>
                      </ErrorBoundary>
                    </ProtectedCanvasRoute>
                  </NavigationProvider>
                }
              />
            }
          />
        ),
      },
      {
        // Dev-only primitives gallery. Unauth, no consent gate — accessible
        // by typing the URL. See DESIGN.md → Building a UI feature.
        path: "/_design-preview",
        element: (
          <Suspense>
            <DesignPreview />
          </Suspense>
        ),
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes, { basename: "/" });
